// Creating in OneBill what ops chose to create: check the plan, pick, apply.
// Omni never changes or deletes an existing OneBill product or price plan. Each
// create copies the setup of the OneBill plan ops chose, and a create that failed
// says why on its row until it succeeds (#2156).
import React, { useMemo, useState } from "react";
import { formatCents, type SyncAction, type SyncItem, type SyncPlan, type SyncResult } from "@phoneware/lens-domain";
import { Icon, type IconName } from "../../icons.js";
import { errorText, plural, whenIn } from "../../format.js";
import { useSession } from "../../session.js";
import { Callout, useToast } from "../../ui.js";
import { useCatalog } from "./context.js";
import { CreateFailure } from "./parts.js";

const SYNC_ORDER: SyncAction[] = ["create", "price_differs", "needs_review", "linked"];

const SYNC_GROUP: Record<SyncAction, { title: string; note: string; icon: IconName; pick: boolean }> = {
  create: {
    title: "Create in OneBill",
    note: "Ops chose to create these. Omni creates each one in OneBill with the setup ops chose to copy, at the Autotask price, checks what OneBill made, then links it.",
    icon: "plus",
    pick: true,
  },
  price_differs: {
    title: "OneBill price differs",
    note: "Linked, but the OneBill plan bills another amount. Omni never changes an existing OneBill plan, so this is for your information.",
    icon: "info",
    pick: false,
  },
  needs_review: {
    title: "Needs a OneBill link",
    note: "Nobody has chosen the OneBill product yet, so these cannot be quoted. Open one to link it.",
    icon: "triangle-alert",
    pick: false,
  },
  linked: { title: "Linked at the Autotask price", note: "Nothing to do.", icon: "check", pick: false },
};

function syncDetail(item: SyncItem): string {
  if (item.action === "price_differs") {
    const onebill = item.onebillPrice === null ? "unknown" : formatCents(item.onebillPrice);
    return `OneBill price ${onebill}, Autotask ${formatCents(item.listPrice)}; Omni orders use the quote price`;
  }
  if (item.action === "needs_review") {
    const best = item.candidates[0];
    if (best?.sameCode) return `Same code in OneBill: ${best.name}, ${best.productCode}`;
    return best ? `Closest in OneBill: ${best.name}, ${Math.round(best.score * 100)}% name match` : "No OneBill product is close by name";
  }
  return item.reason;
}

export function SyncPanel({ onOpen }: { onOpen: (productId: string) => void }): React.ReactElement {
  const { api } = useSession();
  const { reload, list } = useCatalog();
  const toast = useToast();
  const [plan, setPlan] = useState<SyncPlan | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<SyncResult | null>(null);
  const [busy, setBusy] = useState<"plan" | "apply" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Only `create` items can be applied; nothing else in the plan changes OneBill.
  const createIds = useMemo(() => new Set((plan?.items ?? []).filter((i) => i.action === "create").map((i) => i.productId)), [plan]);
  const chosen = [...picked].filter((id) => createIds.has(id));

  async function check(): Promise<void> {
    setBusy("plan");
    setError(null);
    try {
      const next = await api.syncPlan();
      setPlan(next);
      setResult(null);
      setPicked(new Set(next.items.filter((i) => i.action === "create").map((i) => i.productId)));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function apply(): Promise<void> {
    setBusy("apply");
    setError(null);
    try {
      const done = await api.syncApply({ productIds: chosen });
      setResult(done);
      setPlan(null);
      setPicked(new Set());
      toast(`Created and linked ${plural(done.applied.length, "product")} in OneBill${done.failed.length ? `, ${done.failed.length} failed` : ""}.`);
      reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  function toggle(ids: string[], on: boolean): void {
    setPicked((now) => {
      const next = new Set(now);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  /** Marked Create in OneBill before anyone chose what it copies (#2156): the plan lists it under Needs a OneBill link. */
  function waitingForSource(item: SyncItem): boolean {
    return item.action === "needs_review" && list.find((p) => p.id === item.productId)?.billingLink.state === "create";
  }

  return (
    <section className="card sync" data-testid="catalog-sync">
      <div className="card-h">
        <h2>Create in OneBill</h2>
        <span className="note">{plan ? `Planned ${whenIn(plan.generatedAt)}` : "Nothing changes in OneBill until you apply."}</span>
      </div>
      <p className="card-p">
        Omni creates in OneBill only the products ops marked Create in OneBill, then links them. It never changes or deletes an existing OneBill product or price plan,
        because migrated customers bill on them.
      </p>
      {error ? (
        <Callout kind="stop" icon="triangle-alert">
          {error}
        </Callout>
      ) : null}
      {result ? (
        <div className="sync-result" data-testid="sync-result">
          <Callout kind={result.failed.length ? "warn" : "ok"} icon={result.failed.length ? "triangle-alert" : "circle-check"}>
            Created and linked {plural(result.applied.length, "product")} in OneBill {whenIn(result.appliedAt)}.{result.failed.length ? ` ${result.failed.length} failed:` : ""}
          </Callout>
          {result.applied.length || result.failed.length ? (
            <ul className="rows">
              {result.applied.map((a) => (
                <li key={a.productId} className="row sync-done" data-testid={`sync-applied-${a.productId}`}>
                  <div className="row-main">
                    <span className="row-t">{a.name}</span>
                    <span className="row-m">{a.onebill ? `Linked to ${a.onebill.productCode}, plan ${a.onebill.pricePlanId}` : "Linked"}</span>
                  </div>
                  <span className="row-end">
                    <Icon name="circle-check" size={16} />
                  </span>
                </li>
              ))}
              {result.failed.map((f) => (
                <li key={f.item.productId} className="row" data-testid={`sync-failed-${f.item.productId}`}>
                  <div className="row-main">
                    <span className="row-t">{f.item.name}</span>
                    <CreateFailure message={f.error} testId={`sync-failed-error-${f.item.productId}`} />
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {plan ? (
        <div className="sync-groups">
          {SYNC_ORDER.map((action) => {
            // Those waiting for someone to choose what they copy lead their group.
            const items = plan.items.filter((i) => i.action === action).sort((a, b) => Number(waitingForSource(b)) - Number(waitingForSource(a)));
            if (!items.length) return null;
            const group = SYNC_GROUP[action];
            const ids = items.map((i) => i.productId);
            return (
              <div key={action} className="sync-group" data-action={action}>
                <div className="sync-gh">
                  {group.pick ? (
                    <input type="checkbox" aria-label={`All of ${group.title}`} checked={ids.every((id) => picked.has(id))} onChange={(e) => toggle(ids, e.target.checked)} />
                  ) : (
                    <Icon name={group.icon} size={15} />
                  )}
                  <b>{group.title}</b>
                  <span className="chip quiet">{items.length}</span>
                  <span className="sync-note">{group.note}</span>
                </div>
                {action === "linked" ? null : (
                  <ul className="rows">
                    {items.map((item) => {
                      const product = list.find((p) => p.id === item.productId);
                      const waiting = waitingForSource(item);
                      const body = (
                        <>
                          <span className="row-main">
                            <span className="row-t">
                              {item.name}{" "}
                              <span className="code">
                                {item.autotask.kind === "service" ? "Service" : "Product"} {item.autotask.id}
                                {item.onebill ? ` \u00b7 ${item.onebill.productCode}` : ""}
                              </span>
                            </span>
                            {action === "create" && item.copyFrom ? (
                              <span className="row-m ob-copy-from" data-testid={`sync-copy-${item.productId}`}>
                                Copies setup from <b>{item.copyFrom.productCode}</b>
                                {` \u00b7 ${item.copyFrom.planName}`}
                              </span>
                            ) : waiting ? (
                              <span className="row-m" data-testid={`sync-reason-${item.productId}`}>
                                {item.reason}
                              </span>
                            ) : (
                              <span className="row-m">{syncDetail(item)}</span>
                            )}
                            {action === "create" && product && !product.description.trim() ? (
                              <span className="row-m ob-warn" data-testid={`sync-no-description-${item.productId}`}>
                                <Icon name="triangle-alert" size={12} />
                                <span>OneBill requires a description, and this product has none.</span>
                              </span>
                            ) : null}
                            {item.createError ? <CreateFailure message={item.createError} lead="Last try failed: " testId={`sync-error-${item.productId}`} /> : null}
                          </span>
                          <span className="row-end">
                            <span className="amount">
                              {formatCents(item.listPrice)}
                              <small>{item.billing === "monthly" ? "a month" : "once"}</small>
                            </span>
                            {action === "needs_review" ? (
                              <button className="btn small" type="button" data-testid={`sync-open-${item.productId}`} onClick={() => onOpen(item.productId)}>
                                <Icon name={waiting ? "copy" : "link"} size={14} />
                                {waiting ? "Choose setup" : "Link"}
                              </button>
                            ) : null}
                          </span>
                        </>
                      );
                      return (
                        <li key={item.productId} data-testid={`sync-item-${item.productId}`}>
                          {group.pick ? (
                            <label className="row sync-row">
                              <input type="checkbox" checked={picked.has(item.productId)} onChange={(e) => toggle([item.productId], e.target.checked)} />
                              {body}
                            </label>
                          ) : (
                            <div className="row">
                              <span className="sync-gap" />
                              {body}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="btnrow sync-actions">
        <button className="btn" type="button" data-testid="sync-check" disabled={busy !== null} onClick={() => void check()}>
          <Icon name="arrow-right-left" size={15} />
          {busy === "plan" ? "Checking" : plan ? "Check again" : "Check OneBill against Autotask"}
        </button>
        {plan && createIds.size ? (
          <button className="btn primary" type="button" data-testid="sync-apply" disabled={busy !== null || chosen.length === 0} onClick={() => void apply()}>
            <Icon name="plus" size={15} />
            {busy === "apply" ? "Creating" : `Create ${plural(chosen.length, "product")} in OneBill`}
          </button>
        ) : null}
      </div>
    </section>
  );
}
